import { useCallback, useMemo, useRef } from 'react'
import { throttle } from 'lodash'

import {
  findSketchElementFromUuid,
  isCanvasEntityElement,
  SketchElement,
} from '@sketch/inspector'
// Deep path, not the `shares/FrameView` barrel: that barrel re-exports
// components which import this one back, and the cycle breaks the bundler.
// `LayersTree` reaches the inspector the same way for the same reason.
import {
  useInspectorHoveredElement,
  useInspectorContext,
} from '../../shares/FrameView/hooks/inspector'
import {
  useCanvasSelection,
  type EntityInteractions,
  type FramesDict,
} from '@sketch/canvas'
import { useNavigateToEntity } from './useNavigateToEntity'

/**
 * Everything the overlay list needs, resolved once for the whole list.
 *
 * Called once by `PageCanvasFramesOverlays`, so the per-entity hook below takes
 * plain values and stable callbacks and knows nothing about the inspector,
 * routing or the selection store.
 */
export function useEntityOverlaysState() {
  const framesRefs = useRef<FramesDict>({})
  const { setHoveredElement, sketchSceneRootElement } = useInspectorContext()
  const hoveredElement = useInspectorHoveredElement()
  const { selectedEntityUUID, selectElement, selectEntityByUUID } =
    useCanvasSelection()

  const navigateToEntity = useNavigateToEntity()

  /**
   * Select an entity, attaching its scene element when there is one.
   *
   * The overlays work off the renderer's file lists, so all they hold is a
   * UUID and a `PRMarinaNode` - not a `SketchElement`, which only exists as
   * part of a built tree, with the ancestry that the promotion rule walks.
   * While inspecting that tree is right here, so the selection can go out
   * complete rather than arriving without an element and being filled in by
   * `useResolveCanvasSelection` a render later. In view mode there is no tree
   * and no element to attach; nothing there reads one.
   */
  const selectEntity = useCallback(
    (uuid: string) => {
      const element = findSketchElementFromUuid(sketchSceneRootElement, uuid)

      if (element) {
        selectElement(element)
      } else {
        selectEntityByUUID(uuid)
      }
    },
    [selectElement, selectEntityByUUID, sketchSceneRootElement]
  )

  const setHoveredEntity = useCallback(
    (uuid: string) => {
      const element = findSketchElementFromUuid(sketchSceneRootElement, uuid)

      if (element) {
        setHoveredElement(element)
      }
    },
    [setHoveredElement, sketchSceneRootElement]
  )

  // Throttled because a header receives every mouse move over it. One throttle
  // for the list rather than one per entity, so crossing between two headers
  // can't race two independent timers.
  const setHoveredEntityUUID = useMemo(
    () => throttle(setHoveredEntity, 50),
    [setHoveredEntity]
  )

  // Memoised, and holding only the stable callbacks. It is passed to every
  // overlay, so a fresh object here would fail the `React.memo` on each of them
  // and re-render the whole list on any parent render - which is the cost this
  // hook exists to avoid. The hovered entity is deliberately *not* in here: it
  // changes on every mouse move, and is handed to each overlay as an already
  // resolved `isHovered` boolean so only the two that actually flipped re-render.
  const interactions = useMemo<EntityInteractions>(
    () => ({ setHoveredEntityUUID, selectEntity, navigateToEntity }),
    [setHoveredEntityUUID, selectEntity, navigateToEntity]
  )

  const clearSelection = useCallback(() => selectElement(null), [selectElement])

  return {
    framesRefs,
    selectedEntityUUID,
    clearSelection,
    hoveredEntityUUID: toEntityUUID(hoveredElement),
    interactions,
  }
}

/**
 * The id of an element when it is an entity, and null otherwise.
 *
 * The overlays match this against their entity ids, so it has to be an entity's
 * id or nothing. A layer's UUID is not unique - that is what `layerPathHash`
 * exists for - so passing one through could in principle collide with a frame's
 * and light up the wrong overlay. `selectedEntityUUID` is narrowed the same way
 * in the selection store, for the same reason.
 */
function toEntityUUID(element: SketchElement | null) {
  if (!element || !isCanvasEntityElement(element)) return null

  return element.elementUUID
}
